import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Flex,
  Grid,
  Separator,
  Text,
  Tooltip,
} from '@optiaxiom/react';

import { useConnections } from '@frontend/api/hooks';

import { ExtendedTurnstileInstance } from '../types';

type ProductInstancesProps = {
  productName: string;
  productIcon: string;
  initialInstances: ExtendedTurnstileInstance[];
};

export function ProductInstances({
  productName,
  productIcon,
  initialInstances,
}: ProductInstancesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [instances, setInstances] = useState<ExtendedTurnstileInstance[]>(initialInstances);

  useEffect(() => {
    setInstances(initialInstances);
  }, [initialInstances]);

  const { connectionUpdater } = useConnections();

  const handleCheckboxChange = (id: string) => {
    setInstances(
      instances.map(instance =>
        instance.id === id ? { ...instance, selected: !instance.selected } : instance
      )
    );
  };

  const modifiedInstances = useMemo(() => {
    return instances
      .filter(
        instance => instance.selected !== initialInstances.find(i => i.id === instance.id)?.selected
      )
      .map(instance => ({
        id: instance.id,
        selected: instance.selected,
      }));
  }, [instances, initialInstances]);

  const handleSave = () => {
    connectionUpdater.updateConnections(modifiedInstances);
  };

  return (
    <Card py="12" px="16">
      <Disclosure open={isOpen} onOpenChange={setIsOpen} w="full">
        <DisclosureTrigger
          chevronPosition="end"
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${productName} instances`}
          w="full"
          style={{
            padding: 0,
          }}
        >
          <Flex flexDirection="row" gap="24" alignItems="center">
            <Flex
              w="48"
              h="48"
              bg="bg.information.subtle"
              rounded="lg"
              justifyContent="center"
              alignItems="center"
            >
              <img src={productIcon} alt={productName} />
            </Flex>
            <Text fontSize="lg">{productName}</Text>
          </Flex>
        </DisclosureTrigger>
        <DisclosureContent mt="20">
          <Flex gap="20">
            <Separator />
            <Grid gridTemplateColumns="2" gap="16">
              {instances.map(instance =>
                instance.disabled ? (
                  <Tooltip
                    key={instance.id}
                    content="This instance is already connected to another Opal instance"
                  >
                    <Box>
                      <Checkbox
                        checked={instance.selected}
                        disabled={instance.disabled}
                        onChange={() => handleCheckboxChange(instance.id)}
                      >
                        {instance.nickname ?? instance.name}
                      </Checkbox>
                    </Box>
                  </Tooltip>
                ) : (
                  <Checkbox
                    key={instance.id}
                    checked={instance.selected}
                    disabled={instance.disabled}
                    onChange={() => handleCheckboxChange(instance.id)}
                  >
                    {instance.nickname ?? instance.name}
                  </Checkbox>
                )
              )}
            </Grid>
            <Separator />
            <Flex flexDirection="row" gap="16" justifyContent="flex-end">
              <Button
                appearance="subtle"
                size="lg"
                onClick={() => {
                  setInstances(initialInstances);
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                size="lg"
                disabled={!modifiedInstances.length || connectionUpdater.isPending}
                loading={connectionUpdater.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </DisclosureContent>
      </Disclosure>
    </Card>
  );
}
